import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { Message } from "@redotech/redo-model/agents/llm-messages";
import { AiSlideOutChatGlobal } from "@redotech/redo-web/ai/chat/components/slide-out-chat-modal-global";
import { AiChatContext } from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import { ObjectId } from "bson";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useIntercom } from "react-use-intercom";

type SupportThread = {
  _id: string;
  updatedAt: string | Date;
  messages: Message[];
};

export type HelpChatGlobalProps = {
  onChatOpen?: () => void;
  onLoadThread?: (threadId: string, thread: SupportThread) => void;
  onRegisterTriageThreadCallback?: (
    callback: (threadId: string) => void,
  ) => void | (() => void);
};

export const HelpChatGlobal = memo(function HelpChatGlobal({
  onChatOpen,
  onLoadThread,
  onRegisterTriageThreadCallback,
}: HelpChatGlobalProps) {
  const [currentThreadId, setCurrentThreadId] = useState<string | undefined>();
  const [threadsRefreshKey, setThreadsRefreshKey] = useState(0);
  const hasInitializedRef = useRef(false);
  const user = useRequiredContext(UserContext);
  const intercom = useIntercom();
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const { close } = useAiSlidePanel();

  const refreshThreads = useCallback(() => {
    setThreadsRefreshKey((prev) => prev + 1);
  }, []);

  const handleNewTriageThread = useCallback(
    (threadId: string) => {
      setCurrentThreadId(threadId);
      refreshThreads();
    },
    [refreshThreads],
  );

  useEffect(() => {
    if (onRegisterTriageThreadCallback) {
      const cleanup = onRegisterTriageThreadCallback(handleNewTriageThread);
      if (typeof cleanup === "function") {
        return cleanup;
      }
      return undefined;
    }
    return undefined;
  }, [onRegisterTriageThreadCallback, handleNewTriageThread]);

  const threadsLoad = useLoad(async () => {
    if (!user.email) {
      return { threads: [] };
    }
    return await merchantRpcClient.getMerchantSupportAgentThreads({
      userEmail: user.email,
    });
  }, [merchantRpcClient, user.email, threadsRefreshKey]);

  const { clearMessages, clearAssets, setInputValue, stopStream, addMessage } =
    useContext(AiChatContext);

  const startNewConversation = useCallback(() => {
    onChatOpen?.();
    stopStream();
    clearAssets();
    clearMessages();
    setInputValue("");
    addMessage({
      id: new ObjectId().toString(),
      role: "assistant",
      content: "Hi! What can I help you with today?",
    } as any);
    setCurrentThreadId(undefined);
    hasInitializedRef.current = true;
  }, [
    onChatOpen,
    stopStream,
    clearAssets,
    clearMessages,
    setInputValue,
    addMessage,
  ]);

  const handleNewConversation = useCallback(() => {
    // Start a new conversation without toggling the slide panel
    startNewConversation();
  }, [startNewConversation]);

  const { isOpen, panelOptions, clearPanelOptions } = useAiSlidePanel();

  // When the panel opens, always start a new conversation
  useEffect(() => {
    if (isOpen && !hasInitializedRef.current) {
      startNewConversation();
    }

    // Reset initialization flag when panel closes
    if (!isOpen) {
      hasInitializedRef.current = false;
    }
  }, [isOpen, startNewConversation]);

  // Handle prefill input when panel opens with options
  useEffect(() => {
    if (panelOptions?.prefillInput) {
      setInputValue(panelOptions.prefillInput);
      clearPanelOptions();
    }
  }, [panelOptions, setInputValue, clearPanelOptions]);

  const openIntercomAction = useCallback(() => {
    close();
    intercom.showMessages();
  }, [close, intercom]);

  const belowChatButtons = [
    { text: "Message history with team", onClick: openIntercomAction },
  ];

  const loadThreadIntoChat = useCallback(
    (threadId: string, thread: SupportThread) => {
      stopStream();
      clearAssets();
      clearMessages();
      setInputValue("");

      for (const [index, message] of thread.messages.entries()) {
        if (message.role === "tool") continue;

        const rawContent: any = (message as any).content;
        const contentToUse =
          typeof rawContent === "string" || Array.isArray(rawContent)
            ? rawContent
            : String(rawContent ?? "");

        if (typeof contentToUse === "string" && contentToUse.trim() === "") {
          continue;
        }

        addMessage({
          id: message.id ?? new ObjectId().toString() + index,
          role: message.role,
          content: contentToUse as any,
        } as any);
      }

      onLoadThread?.(threadId, thread);
      setCurrentThreadId(threadId);
    },
    [
      stopStream,
      clearAssets,
      clearMessages,
      setInputValue,
      addMessage,
      onLoadThread,
    ],
  );

  const threads = threadsLoad.value?.threads || [];

  return (
    <AiSlideOutChatGlobal
      belowChatButtons={belowChatButtons}
      currentThreadId={currentThreadId}
      onClose={close || (() => {})}
      onLoadThread={loadThreadIntoChat}
      onNewConversation={handleNewConversation}
      threads={threads}
    />
  );
});
